import {
  AfterViewInit,
  Component,
  ElementRef, EventEmitter,
  Input,
  OnDestroy, Output,
  ViewChild
} from '@angular/core';
import { AnyObject, ConfigMeasurementUnit, Property } from '@mri/types';
import { AuthResourceService, ConfigResourceService, GenericResourceService } from '@mri/app/resources';
import { delay, filter, forkJoin, map, mergeMap, Observable, of, Subject, Subscription, tap } from 'rxjs';
import { getEndToday, getStartToday, getTodayDate } from '@mri/functions';
import * as echarts from 'echarts';
import { EChartsType } from 'echarts';
import { ECBasicOption } from 'echarts/types/dist/shared';
import { catchError } from 'rxjs/operators';


export enum SourceBaseOriginReport {
  POSITION = 'position',
  DEVICE = 'device',
  CONSOLIDATE = 'consolidate',
  MOBILE_CURRENT = 'mobile-current',
  ALARM_EVENT = 'alarm-event'
}

export type ChartViewSourceParam = {
  key: string;
  value?: string;
}

export type ChatViewSourceBase = {
  label?: string;
  originName: SourceBaseOriginReport;
  filters: Record<string, AnyObject>;
  sort?: Record<string, 'asc' | 'desc'>;
  limit?: number;
  metrics: Array<ChartViewSourceParam>;
  dimensions: Array<ChartViewSourceParam>;
  metricValue: string;
}

export type ChartViewCategory = {
  key: string;
  label: string;
  color?: string;
  colorResults?: Record<string, string>;
  dataType?: 'number' | 'string' | 'date';
  showForm?: string;
  suffix?: string;
}

export type ChartValueConfig = {
  prefix?: string;
  suffix?: string;
  unitKey?: string;
}

export type ChartViewConfig = {
  type: 'circle' | 'line' | 'bar' | 'count' | 'table'
  specialValues?: Record<string, string>;
  sourceBases: Array<ChatViewSourceBase>;
  categoryConfig?: ChartViewCategory;
  seriesConfig: ChartViewCategory;
  groupBy?: string[];
  valueConfig?: ChartValueConfig;
}

export const urlReportMap: Record<SourceBaseOriginReport, string> = {
  [SourceBaseOriginReport.ALARM_EVENT]: 'mobiles/alarms/events/report',
  [SourceBaseOriginReport.DEVICE]: 'devices/report',
  [SourceBaseOriginReport.POSITION]: 'positions/report',
  [SourceBaseOriginReport.MOBILE_CURRENT]: 'mobiles/currents/report',
  [SourceBaseOriginReport.CONSOLIDATE]: 'consolidates/report'
};


function useSpecialValue(value: AnyObject, specialValues?: Record<string, string | undefined>): AnyObject {
  if (value === '$shortDayToday') return getTodayDate();
  if (value === '$startToday') return getStartToday();
  if (value === '$endToday') return getEndToday();

  if (specialValues && specialValues?.[value.toString()]) return specialValues[value.toString()];

  return value;
}

type DataChartGlobal = {
  value: number;
  [key: string]: AnyObject;
}

type DataChartItem = {
  serie: string;
  $sufix?: string;
  [key: string]: AnyObject;
}


function toDataChartGlobal(data: AnyObject[], sourceConfig: ChatViewSourceBase): Array<DataChartGlobal> {
  return data.map(d => {
    const value = d[sourceConfig.metricValue];
    const args: Record<string, AnyObject> = {};
    const $sourceOrigin = sourceConfig.originName;
    const $sourceLabel = sourceConfig.label;

    sourceConfig.dimensions
      .forEach(
        (sourceParam, i) => args[`$dimension${i}`] = d[sourceParam.key]
      );

    sourceConfig.metrics
      .forEach(
        (sourceParam, i) => args[`$metric${i}`] = d[sourceParam.key]
      );

    return { value, $sourceOrigin, $sourceLabel, ...args };
  });
}


@Component({
  selector: 'app-chart-view',
  standalone: true,
  imports: [],
  template: `

    <div #chart class="chart-pane" style="width: 100% !important; height: 400px;">
      @if (isLoading && !isLoaded) {
        <div class="w-full h-full animate-pulse rounded-lg flex items-center justify-center">
          <!-- Barras ficticias del skeleton -->
          <div class="w-11/12 h-5/6 flex items-end justify-between">
            <div class="w-6 bg-gray-300 rounded" style="height: 40%;"></div>
            <div class="w-6 bg-gray-300 rounded" style="height: 70%;"></div>
            <div class="w-6 bg-gray-300 rounded" style="height: 55%;"></div>
            <div class="w-6 bg-gray-300 rounded" style="height: 80%;"></div>
            <div class="w-6 bg-gray-300 rounded" style="height: 65%;"></div>
          </div>
        </div>
      }
    </div>`,
  styles: [
    `
      .chart-pane {
        width: 100% !important;
        height: 400px;
      }
    `
  ]
})
export class ChartViewComponent implements OnDestroy, AfterViewInit {
  private _sub = new Subscription();
  private _config: ChartViewConfig;
  private _obtainData$ = new Subject<Array<ChatViewSourceBase>>();
  private _initInstance$ = new Subject();
  private _initialized = false;
  private _series: string[] = [];
  private _categories: string[] = [];
  private _authProperty?: Property;
  private _units?: Map<string, ConfigMeasurementUnit>;
  private _autoUpdateTime = 30000;
  private _autoUpdate: boolean | undefined = true;

  @ViewChild('chart', { static: false }) chartEl!: ElementRef;
  private _chartInstance: EChartsType;

  isLoading: boolean = false;
  isLoaded: boolean = false;

  @Input({ required: true }) set config(value: ChartViewConfig) {
    this._config = value;
    if (!this._initialized) return;

    this._obtainData$.next(this._config.sourceBases);
  }

  @Input() set autoUpdateTime(value: number) {
    this._autoUpdateTime = value || 3000;
  };

  @Input() set autoUpdate(value: boolean | undefined) {
    this._autoUpdate = value;
    if (!this._autoUpdate) return;

    this._obtainData$.next(this._config.sourceBases);
  };

  @Output() onLoading = new EventEmitter<boolean>();

  constructor(
    private _resourceSrv: GenericResourceService,
    private _authSrv: AuthResourceService,
    private _configSrv: ConfigResourceService
  ) {
    this._sub.add(
      this._obtainData$
        .pipe(
          filter(() => !this.isLoading),
          tap(() => this.isLoading = true),
          tap(() => this.onLoading.next(true)),

          map(configs => configs.map(s => this._obtainDataOfResource(s))),
          mergeMap(source$ => forkJoin(source$)),
          tap(data => this._obtainSeries(data.flat())),
          tap(data => this._obtainCategories(data.flat())),
          map(data => this._getDataProcessed(data.flat())),
          //tap(result => console.log('> Result', result)),
          map(result => this._getChartData(result)),
          //tap(chartConfig => console.log('chartConfig', chartConfig) ),
          tap(chartConfig => this._chartInstance.setOption(chartConfig)),
          tap(() => this.isLoading = false),
          tap(() => this.isLoaded = true),

          tap(() => this.onLoading.next(false)),

          delay(this._autoUpdateTime),
          tap(() => this._autoUpdate && this._obtainData$.next(this._config.sourceBases)),
          catchError(() => {
            this.isLoading = false;
            return of(null);
          })
        )
        .subscribe()
    );

    this._sub.add(
      this._initInstance$
        .pipe(
          mergeMap(() => this._authSrv.authFirst$),
          tap(value => this._authProperty = value?.property),

          mergeMap(() => this._configSrv.units$),
          tap(value =>
            this._units = new Map(value.map(u => [u.key, u]))
          ),

          delay(500),
          tap(() => {
            this._chartInstance = echarts.init(this.chartEl.nativeElement, null, {
              renderer: 'svg',
              useDirtyRect: false
            });

            this._obtainData$.next(this._config.sourceBases);
          }),
          tap(() => this._initialized = true)
        )
        .subscribe()
    );
  }

  ngOnDestroy() {
    this._sub.unsubscribe();
  }

  ngAfterViewInit() {
    this._initInstance$.next({});
  }

  reload() {
    this._obtainData$.next(this._config.sourceBases);
  }

  private _obtainDataOfResource(prop: ChatViewSourceBase): Observable<Array<DataChartGlobal>> {
    const { originName, filters, metrics, dimensions, sort, label, limit } = prop;

    const url = urlReportMap[originName];
    const filter = { ...(filters || {}) };

    Object.entries(filters)
      .forEach(([key, value]) =>
        Object.assign(filter, { [key]: useSpecialValue(value) })
      );

    metrics
      .forEach(({ key, value }) =>
        Object.assign(filter, { [`$metric:${key}`]: useSpecialValue(value || '') })
      );

    dimensions
      .forEach(({ key, value }) =>
        Object.assign(filter, { [`$dimension:${key}`]: useSpecialValue(value || '') })
      );

    Object.entries(sort || {})
      .forEach(([key, value]) => Object.assign(filter, { [`$sort:${key}`]: value }));

    return this._resourceSrv.getElements<AnyObject>(url, { filter, paginate: { page: 1, size: limit || 9999999999 } })
      .pipe(
        map(result => result.objects),
        map(results => results.map(r => ({ ...r, sourceLabel: label, sourceType: originName }))),
        map(results => toDataChartGlobal(results, prop))
      );
  }

  private _obtainCategories(data: Array<AnyObject>): string[] {
    const sConfig = this._config.categoryConfig;
    if (!sConfig) return this._categories = ['value'];

    return this._categories = [
      ...new Set(
        data.map(({ value, ...d }) =>
          useSpecialValue(sConfig.label || 'value', { ...d, ...(this._config.specialValues || {}) }).toString()
        )
      )
    ];
  }

  private _obtainSeries(data: Array<AnyObject>): string[] {
    const sConfig = this._config.seriesConfig;

    return this._series = [
      ...new Set(
        data.map(({ value, ...d }) =>
          useSpecialValue(sConfig.label || 'main', { ...d, ...(this._config.specialValues || {}) }).toString()
        )
      )
    ];
  }

  private _getDataProcessed(data: Array<DataChartGlobal>): Array<DataChartItem> {

    const mapData = new Map<string, DataChartItem>();
    const sConfig = this._config.seriesConfig;
    const cConfig = this._config.categoryConfig;
    const vConfig = this._config.valueConfig;

    const initSpecialValues = {
      ...(this._config.specialValues || {}),
      $speedUnitSystem: this._authProperty?.config?.speedUnit,
      $distanceUnitSystem: this._authProperty?.config?.distanceUnit
    };

    const unitKey = vConfig ?
      useSpecialValue(vConfig.unitKey, initSpecialValues)?.toString() :
      '';
    const unit = this._units?.get(unitKey);

    data.forEach(({ value: valueData, ...d }) => {

      const specialValues = {
        ...d, ...(initSpecialValues)
      };

      const serieValue = sConfig ?
        useSpecialValue(sConfig.label, specialValues)?.toString() :
        '$serie';

      const categoryKey = cConfig ?
        useSpecialValue(cConfig.label, specialValues)?.toString() :
        'value';

      const prevData = mapData.get(serieValue.toString());
      const prevValue = prevData?.[categoryKey.toString()] ?? 0;

      const valueResult = (unit?.multiply || 1) * valueData;

      mapData.set(serieValue, {
        ...(prevData || {}),
        serie: serieValue,
        $sufix: unit?.abbr,
        [categoryKey.toString()]: valueResult + prevValue
      });
    });

    return [...mapData.values()];
  }

  private _generateChartPie(data: Array<DataChartItem>): ECBasicOption {
    const colorSeries = this._config.seriesConfig.colorResults || {};
    const [firstCategory] = this._categories;
    const total = data.reduce((sum, d) => sum + Number(d?.[firstCategory] ?? 0), 0);

    return {

      title: {
        subtext: `Total: ${total}`,
        fontWeight: 'bold',

        left: 'center',
        bottom: '5%'
      },
      tooltip: {
        trigger: 'item'
      },

      legend: {
        top: '5%',
        left: 'center',
        type: 'scroll', // ← Habilita scroll
        orient: 'horizontal', // Scroll horizontal
        formatter: function(name: string) {
          const value = Number(data.find(d => d.serie === name)?.[firstCategory] ?? 0);

          const percent = ((value / total) * 100).toFixed(1);
          return `${name}: ${value} (${percent}%)`;
        }
      },
      series: [
        {

          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          padAngle: 2,
          itemStyle: {
            borderRadius: 10
          },
          label: {
            show: false,
            position: 'center',
            //position: 'outside',
            formatter: '{b}: {c} ({d}%)'
          },
          labelLine: {
            show: true
          },
          data: data.map(d => ({
            value: d[firstCategory],
            name: d.serie,
            itemStyle: { color: colorSeries[d.serie] }
          }))
        }
      ]
    };
  }

  private _generateChartLine(data: Array<DataChartItem>): ECBasicOption {
    return {
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        data: ['Email', 'Union Ads', 'Video Ads', 'Direct', 'Search Engine']
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: 'Email',
          type: 'line',
          stack: 'Total',
          data: [120, 132, 101, 134, 90, 230, 210]
        },
        {
          name: 'Union Ads',
          type: 'line',
          stack: 'Total',
          data: [220, 182, 191, 234, 290, 330, 310]
        },
        {
          name: 'Video Ads',
          type: 'line',
          stack: 'Total',
          data: [150, 232, 201, 154, 190, 330, 410]
        },
        {
          name: 'Direct',
          type: 'line',
          stack: 'Total',
          data: [320, 332, 301, 334, 390, 330, 320]
        },
        {
          name: 'Search Engine',
          type: 'line',
          stack: 'Total',
          data: [820, 932, 901, 934, 1290, 1330, 1320]
        }
      ]
    };
  }

  private _generateChartBar(data: Array<DataChartItem>): ECBasicOption {
    const categories = this._categories;

    return {
      tooltip: {
        trigger: 'item',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        data: data.map(d => d.serie),
        right: 20,
        left: 20,
        bottom: 20,          // Coloca la leyenda en la parte superior del canvas
        type: 'scroll', // ← Habilita scroll
        orient: 'horizontal' // Scroll horizontal,
      },
      grid: {
        top: 50,         // Deja espacio suficiente debajo de la leyenda
        left: 50,
        right: 50,
        bottom: 70
      },
      xAxis: {
        type: 'category',
        data: categories.map((c, i) => `${c} ${data[i].$sufix ?? ''}`),
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value'
      },
      series: data.map(d => ({
        name: `${d.serie}`,
        type: 'bar',
        barGap: 0.5,
        radius: ['40%', '70%'],
        itemStyle: {
          borderRadius: [10, 10, 0, 0] // Top-left, top-right, bottom-right, bottom-left
        },
        markPoint: {
          data: [
            { type: 'max', name: 'Máximo' },
            { type: 'min', name: 'Mínimo' }
          ],
          label: {
            show: true,
            formatter: '{c}' // Muestra solo el valor
          },
          symbol: 'pin',
          symbolSize: 40

        },
        data: categories.map(c => Number(d[c]).toFixed(2))
      }))
    };
  }

  private _getChartData(data: Array<DataChartItem>): ECBasicOption {
    const type = this._config.type;

    if (type === 'line')
      return this._generateChartLine(data);

    if (type === 'circle')
      return this._generateChartPie(data);

    if (type === 'bar')
      return this._generateChartBar(data);

    return {};
  }

}
